import { useCallback, useEffect } from 'react';
import {
  ApiResponseStatus,
  type SuprSendFeedProviderProps,
  useFeedClient,
  useFeedData
} from '@suprsend/react';
import { useIsMutating, useMutation } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useAtom } from 'jotai';

import { fetchNotifications } from '@/adapters/fetchExaCare';
import { useSnackbar } from '@/hooks/useSnackbar';

import {
  type Notification,
  notificationFromSuprSend
} from '../components/notificationFromSuprSend';

import { notificationsArchivedBeforeAtom } from './notificationsArchivedBeforeAtom';
import { notificationsMarkedAllReadAtAtom } from './notificationsMarkedAllReadAtAtom';
import { notificationsUnreadOffsetAtom } from './notificationsUnreadOffsetAtom';

interface ArchiveAllVariables {
  messageIds: string[];
  previousArchivedBefore: number | null;
}

interface InboxArchiveAllResponse {
  archived_before: string;
}

const MAX_ARCHIVE_ALL_MESSAGE_IDS = 500;
const MAX_UNREAD_PREFETCH_NOTIFICATIONS = 100;
const ARCHIVE_ALL_MUTATION_KEY = ['archiveAllNotifications'];

const ARCHIVE_ALL_FAILED_MESSAGE =
  "Couldn't delete all notifications. Please try again.";

export const NOTIFICATION_FEED_OPTIONS = {
  stores: [
    { storeId: 'all', label: 'All' },
    { storeId: 'unread', label: 'Unread', query: { read: false } }
  ]
} satisfies Omit<SuprSendFeedProviderProps, 'children'>;

export function useNotificationFeed() {
  const feedClient = useFeedClient();
  const feedData = useFeedData();
  const { showSnackbar } = useSnackbar();
  const isArchivingAll =
    useIsMutating({ mutationKey: ARCHIVE_ALL_MUTATION_KEY }) > 0;
  const [archivedBefore, setArchivedBefore] = useAtom(
    notificationsArchivedBeforeAtom
  );

  const [markedAllReadAt, setMarkedAllReadAt] = useAtom(
    notificationsMarkedAllReadAtAtom
  );
  const [unreadOffset, setUnreadOffset] = useAtom(
    notificationsUnreadOffsetAtom
  );
  const rawNotifications = feedData?.notifications;
  const serverUnreadCount = feedData?.meta?.unread;

  const loadedNotifications = (rawNotifications ?? []).map((notification) =>
    notificationFromSuprSend(
      markedAllReadAt !== null &&
        !notification.read_on &&
        notification.created_on <= markedAllReadAt
        ? { ...notification, read_on: markedAllReadAt }
        : notification
    )
  );
  const notifications = loadedNotifications.filter(
    (notification) =>
      archivedBefore === null || notification.created_on > archivedBefore
  );
  const oldestLoadedCreatedOn = loadedNotifications.at(-1)?.created_on;
  const hasReachedArchived =
    archivedBefore !== null &&
    oldestLoadedCreatedOn !== undefined &&
    oldestLoadedCreatedOn <= archivedBefore;
  const isLoading =
    !feedData ||
    feedData.apiStatus === ApiResponseStatus.INITIAL ||
    feedData.apiStatus === ApiResponseStatus.LOADING;
  const isFetchingMore =
    feedData?.apiStatus === ApiResponseStatus.FETCHING_MORE;
  const hasMore = !hasReachedArchived && Boolean(feedData?.pageInfo.hasMore);
  const hasNotifications = notifications.length > 0;
  const isEmpty = !isLoading && !hasNotifications && !hasMore;
  const canMarkAllAsRead =
    hasMore || notifications.some((notification) => !notification.read_on);
  const canArchiveAll =
    !isEmpty && !isLoading && !isFetchingMore && !isArchivingAll;
  const unreadCount =
    typeof serverUnreadCount === 'number'
      ? Math.max(
          0,
          serverUnreadCount -
            (unreadOffset.serverUnreadCount === serverUnreadCount
              ? unreadOffset.count
              : 0)
        )
      : notifications.filter((notification) => !notification.read_on).length;
  const hasUnloadedUnread =
    hasMore &&
    feedData?.apiStatus !== ApiResponseStatus.ERROR &&
    loadedNotifications.length < MAX_UNREAD_PREFETCH_NOTIFICATIONS &&
    notifications.filter((notification) => !notification.read_on).length <
      unreadCount;

  useEffect(() => {
    setUnreadOffset((previous) =>
      previous.serverUnreadCount === serverUnreadCount
        ? previous
        : { serverUnreadCount, count: 0 }
    );
  }, [serverUnreadCount, setUnreadOffset]);

  useEffect(() => {
    if (markedAllReadAt === null) {
      return;
    }
    for (const notification of rawNotifications ?? []) {
      if (!notification.read_on && notification.created_on <= markedAllReadAt) {
        void feedClient?.markAsRead(notification.n_id);
      }
    }
  }, [feedClient, rawNotifications, markedAllReadAt]);

  const fetchNextPage = useCallback(() => {
    if (hasMore && !isFetchingMore && !isArchivingAll) {
      feedClient?.fetchNextPage();
    }
  }, [feedClient, hasMore, isFetchingMore, isArchivingAll]);

  const offsetUnreadCount = (notification: Notification) => {
    if (notification.read_on) {
      return;
    }
    setUnreadOffset((previous) => ({
      serverUnreadCount,
      count:
        (previous.serverUnreadCount === serverUnreadCount
          ? previous.count
          : 0) + 1
    }));
  };

  const markAllAsRead = () => {
    setMarkedAllReadAt(dayjs().valueOf());
    setUnreadOffset({ serverUnreadCount, count: serverUnreadCount ?? 0 });
    return feedClient?.markAllAsRead();
  };

  const markAsInteracted = (notification: Notification) => {
    offsetUnreadCount(notification);
    return feedClient?.markAsInteracted(notification.n_id);
  };

  const archiveNotification = (notification: Notification) => {
    offsetUnreadCount(notification);
    return feedClient?.markAsArchived(notification.n_id);
  };

  const archiveAllMutation = useMutation({
    mutationKey: ARCHIVE_ALL_MUTATION_KEY,
    mutationFn: ({ messageIds }: ArchiveAllVariables) =>
      fetchNotifications.post<InboxArchiveAllResponse>('/inbox/archive-all', {
        message_ids: messageIds
      }),
    onSuccess: (response) => {
      setArchivedBefore(dayjs(response.archived_before).valueOf());
    },
    onError: (_error, { previousArchivedBefore }) => {
      setArchivedBefore(previousArchivedBefore);
      showSnackbar({
        message: ARCHIVE_ALL_FAILED_MESSAGE,
        severity: 'error'
      });
    }
  });

  const archiveAll = async () => {
    if (isArchivingAll) {
      return;
    }
    const previousArchivedBefore = archivedBefore;
    setArchivedBefore(dayjs().valueOf());
    await archiveAllMutation
      .mutateAsync({
        messageIds: loadedNotifications
          .slice(0, MAX_ARCHIVE_ALL_MESSAGE_IDS)
          .map((notification) => notification.n_id),
        previousArchivedBefore
      })
      .catch(() => undefined);
  };

  return {
    notifications,
    unreadCount,
    canMarkAllAsRead,
    canArchiveAll,
    isLoading,
    isFetchingMore,
    isEmpty,
    isArchivingAll,
    hasUnloadedUnread,
    fetchNextPage,
    markAllAsRead,
    markAsInteracted,
    archiveNotification,
    archiveAll
  };
}
